'use client';

import { c as _c } from "react/compiler-runtime";
import { GripVertical, X } from 'lucide-react';
import { MediaSelector } from '@/components/ui/media-selector';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
interface ImageGalleryManagerProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  title?: string;
  description?: string;
  emptyStateMessage?: string;
  gridCols?: 2 | 3 | 4;
  allowUpload?: boolean;
  allowBrowse?: boolean;
}
function SortableImage(t0) {
  const $ = _c(23);
  const {
    id,
    index,
    url,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== id || $[1] !== index) {
    t1 = {
      id,
      index
    };
    $[0] = id;
    $[1] = index;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    ref,
    handleRef,
    isDragSource
  } = useSortable(t1);
  const t2 = isDragSource ? 0.3 : 1;
  let t3;
  if ($[3] !== t2) {
    t3 = {
      opacity: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `Gallery image ${index + 1}`;
  let t5;
  if ($[5] !== t4 || $[6] !== url) {
    t5 = <img src={url} alt={t4} className="w-full h-full object-cover rounded-lg border-2 border-gray-200 select-none pointer-events-none" draggable={false} />;
    $[5] = t4;
    $[6] = url;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <GripVertical className="w-4 h-4" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== handleRef) {
    t7 = <button ref={handleRef} type="button" className="absolute top-1 left-1 bg-black/50 text-white rounded-md p-1 cursor-grab active:cursor-grabbing touch-none" aria-label="Drag to reorder">{t6}</button>;
    $[9] = handleRef;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== index || $[12] !== onRemove) {
    t8 = () => onRemove(index);
    $[11] = index;
    $[12] = onRemove;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <X className="w-4 h-4" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8) {
    t10 = <button type="button" onClick={t8} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100 cursor-pointer" title="Remove image">{t9}</button>;
    $[15] = t8;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== ref || $[18] !== t10 || $[19] !== t3 || $[20] !== t5 || $[21] !== t7) {
    t11 = <div ref={ref} className="relative group aspect-square" style={t3}>{t5}{t7}{t10}</div>;
    $[17] = ref;
    $[18] = t10;
    $[19] = t3;
    $[20] = t5;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}
export function ImageGalleryManager(t0) {
  const $ = _c(31);
  const {
    images,
    onImagesChange,
    title: t1,
    description,
    emptyStateMessage: t2,
    gridCols: t3,
    allowUpload: t4,
    allowBrowse: t5
  } = t0;
  const title = t1 === undefined ? "Gallery Media" : t1;
  const emptyStateMessage = t2 === undefined ? "No images added yet" : t2;
  const gridCols = t3 === undefined ? 3 : t3;
  t4 === undefined ? true : t4;
  const allowBrowse = t5 === undefined ? true : t5;
  let t6;
  if ($[0] !== images) {
    t6 = images.map(_temp);
    $[0] = images;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const itemIds = t6;
  let t7;
  if ($[2] !== images || $[3] !== onImagesChange) {
    t7 = urls => {
      if (!urls) {
        return;
      }
      const newImages = Array.isArray(urls) ? urls : [urls];
      const uniqueNewImages = newImages.filter(url_0 => !images.includes(url_0));
      if (uniqueNewImages.length === 0) {
        return;
      }
      onImagesChange([...images, ...uniqueNewImages]);
    };
    $[2] = images;
    $[3] = onImagesChange;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const handleAddImages = t7;
  let t8;
  if ($[5] !== images || $[6] !== onImagesChange) {
    t8 = index => {
      onImagesChange(images.filter((_, i_0) => i_0 !== index));
    };
    $[5] = images;
    $[6] = onImagesChange;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const handleRemoveImage = t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4"
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const gridColsClass = t9[gridCols];
  let t10;
  if ($[9] !== title) {
    t10 = <h3 className="font-medium">{title}</h3>;
    $[9] = title;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== description) {
    t11 = description && <p className="text-sm text-gray-500">{description}</p>;
    $[11] = description;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== allowBrowse || $[14] !== handleAddImages) {
    t12 = <MediaSelector value={null} onChange={handleAddImages} label="Add Images" mode="selector-only" showBrowseLibrary={allowBrowse} allowMultiple={true} />;
    $[13] = allowBrowse;
    $[14] = handleAddImages;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== gridColsClass || $[17] !== handleRemoveImage || $[18] !== images || $[19] !== itemIds || $[20] !== onImagesChange) {
    t13 = images.length > 0 && <DragDropProvider onDragEnd={event => {
      if (event.canceled) {
        return;
      }
      const {
        source
      } = event.operation;
      if (isSortable(source)) {
        const {
          initialIndex,
          index: index_0
        } = source;
        if (initialIndex !== index_0) {
          const newImages_0 = [...images];
          const [removed] = newImages_0.splice(initialIndex, 1);
          newImages_0.splice(index_0, 0, removed);
          onImagesChange(newImages_0);
        }
      }
    }}><div className={`grid ${gridColsClass} gap-3 mt-4`}>{images.map((imageUrl, index_1) => <SortableImage key={itemIds[index_1]} id={itemIds[index_1]} index={index_1} url={imageUrl} onRemove={handleRemoveImage} />)}</div><DragOverlay>{source_0 => {
          const idx = itemIds.indexOf(source_0.id as string);
          const url_1 = idx >= 0 ? images[idx] : null;
          if (!url_1) {
            return null;
          }
          return <div className="aspect-square opacity-90 shadow-xl rounded-lg rotate-2"><img src={url_1} alt="Dragging" className="w-full h-full object-cover rounded-lg border-2 border-blue-400" draggable={false} /></div>;
        }}</DragOverlay></DragDropProvider>;
    $[16] = gridColsClass;
    $[17] = handleRemoveImage;
    $[18] = images;
    $[19] = itemIds;
    $[20] = onImagesChange;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== emptyStateMessage || $[23] !== images.length) {
    t14 = images.length === 0 && <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg"><p className="text-sm text-gray-500">{emptyStateMessage}</p></div>;
    $[22] = emptyStateMessage;
    $[23] = images.length;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12 || $[28] !== t13 || $[29] !== t14) {
    t15 = <div className="space-y-3">{t10}{t11}{t12}{t13}{t14}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp(url, i) {
  return `${i}-${url}`;
}